/**
 * @file hooks/useChatThread.ts
 * Orchestration du fil conversationnel.
 *
 * Câble l'UI chat sur le SDK Assistia :
 *   - upload d'un PDF        → sdk.dossiers.upload()      (bulle document + cochettes)
 *   - flux temps réel        → sdk.agentStream.subscribe() (SSE + fallback simulation)
 *   - question/réponse RAG    → sdk.rag.query()
 *   - validation humaine      → sdk.hitl.validate()
 *
 * Mapping des événements SSE → cochettes WhatsApp :
 *   ingest/ocr  → ✓✓ grise  (traitement démarré)
 *   classify    → badge de classification sur la bulle document
 *   hitl        → carte de validation inline
 *   done        → ✓✓ bleue  (analyse terminée) + carte de résultat enrichie
 *   error       → message d'erreur
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSDK } from '../context/SDKContext.tsx';
import { useAuth } from '../context/AuthContext.tsx';
import { clearanceAllows, parseClearance } from '../utils/clearance.ts';
import { chatStore } from '../utils/threadStorage.ts';
import type { ChatEvent, AssistiaSDKResponse, RagQueryResponse } from '@assistia/sdk';
import { AccessDeniedError, SecurityErrorType, conversationStream } from '@assistia/sdk';
import { useChatConfig } from './useChatConfig.ts';
import type {
  ChatMessage,
  DocumentMessage,
  HitlMessage,
  ResultCardMessage,
  StatusDebugEntry,
  TextMessage,
} from '../components/chat/types.ts';

// ── Helpers ─────────────────────────────────────────────────────────────────

let seq = 0;
const newId = (): string => `m-${Date.now()}-${seq++}`;

function humanFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

/**
 * Détecte un blocage guardrail, qu'il provienne d'une erreur jetée (legacy)
 * ou de la nouvelle `SecurityResponse` (SDK 6.8 : `blocked: true` /
 * `error.type === BLOCKED`).
 */
function isSecurityBlocked(
  err: unknown,
): err is { reason?: string; blocked?: boolean; error?: { type?: SecurityErrorType } } {
  if (typeof err !== 'object' || err === null) return false;
  const e = err as { name?: string; blocked?: boolean; error?: { type?: SecurityErrorType } };
  return (
    e.name === 'SecurityBlockedError' ||
    e.blocked === true ||
    e.error?.type === SecurityErrorType.BLOCKED
  );
}

/**
 * 6.8-03 — Mapping typé des erreurs de sécurité (403) vers un message UI.
 *
 * - Blocage guardrail (backend 6.8-01) — le contenu lui-même est rejeté,
 *   NE PAS réessayer tel quel.
 * - AccessDeniedError / status 403 : auth ou licence insuffisante.
 * - Retourne null si l'erreur n'est pas un cas sécurité (l'appelant garde
 *   son traitement générique).
 */
function securityBlockText(err: unknown): string | null {
  if (isSecurityBlocked(err)) {
    const reason =
      (err as { reason?: string }).reason ?? err.error?.type ?? 'contenu non conforme';
    return `Message bloqué par la politique de sécurité (${reason}). ` +
      "Reformulez votre demande — la renvoyer à l'identique sera de nouveau refusée.";
  }
  if (err instanceof AccessDeniedError || (err as { status?: number }).status === 403) {
    return "Accès refusé : votre habilitation ou votre licence ne permet pas cette action.";
  }
  return null;
}


/** Extraction défensive de la classification depuis un payload SSE. */
function extractClassification(p: Record<string, unknown>): string | undefined {
  return (
    str(p.classification) ??
    str(p.label) ??
    str(p.level) ??
    str((p.result as Record<string, unknown> | undefined)?.classification)
  );
}

/** Extraction défensive d'un résumé exécutif. */
function extractSummary(p: Record<string, unknown>): string {
  const result = (p.result ?? {}) as Record<string, unknown>;
  return (
    str(p.summary) ??
    str(p.executive_summary) ??
    str(result.summary) ??
    str(result.executive_summary) ??
    'Document analysé. Les données ont été extraites et injectées en base.'
  );
}

/** Extraction défensive des métadonnées (objet plat label/valeur). */
function extractMetadata(p: Record<string, unknown>): Array<{ label: string; value: string }> {
  const raw =
    (p.metadata as Record<string, unknown> | undefined) ??
    ((p.result as Record<string, unknown> | undefined)?.metadata as Record<string, unknown> | undefined) ??
    {};
  return Object.entries(raw)
    .filter(([, v]) => v != null && typeof v !== 'object')
    .map(([label, v]) => ({ label, value: String(v) }));
}

// ── Hook ────────────────────────────────────────────────────────────────────

/** Résultat renvoyé par uploadAttachment (miroir du body 200 de l'API). */
export interface AttachmentUploadResult {
  attachment_id: string;
  filename: string;
  size: number;
  mime_type: string;
  expires_at: string;
}

export interface ChatThread {
  messages: ChatMessage[];
  /** L'agent traite-t-il en ce moment ? (typing indicator) */
  agentBusy: boolean;
  /** Libellé de l'activité courante de l'agent */
  agentActivity: string;
  /** dossier courant (dernier uploadé) — cible des questions RAG */
  activeDossierId: string | null;
  /** Une décision HITL est-elle en cours d'envoi ? */
  hitlBusy: boolean;
  /**
   * Envoie un message texte.
   * @param attachmentId — CHAT-03 : id retourné par uploadAttachment (optionnel).
   */
  sendText: (text: string, attachmentId?: string | null) => void;
  attachFile: (file: File) => void;
  /**
   * CHAT-03 — Upload d'une pièce jointe conversationnelle vers POST /api/v1/chat/attachment.
   * Retourne les métadonnées (attachment_id, filename, size, mime_type, expires_at).
   * Lève une Error en cas d'échec (413, 415, 429…).
   */
  uploadAttachment: (file: File) => Promise<AttachmentUploadResult>;
  decideHitl: (dossierId: string, approved: boolean) => void;
  /** 7.3-02 — Relance la dernière requête (texte ou document). */
  retryLast: () => void;
  /** 7.3-02 — Ré-émet le texte d'un message utilisateur existant. */
  resendMessage: (id: string) => void;
  /** 7.3-06 — Sauvegarde immédiate (avant bascule de conversation). */
  flush: () => Promise<void>;
}

/**
 * @param conversationId 7.3-06 — fil à hydrater/persister :
 *  - `string` : conversation explicite (multi-fils) ;
 *  - `undefined` : conversation active du store (compat 7.3-05) ;
 *  - `null` : liste pas encore chargée → pas d'hydratation.
 */
export function useChatThread(conversationId?: string | null): ChatThread {
  const { sdk, demoMode } = useSDK();
  const { clearance, getToken } = useAuth();
  const { config } = useChatConfig();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [agentBusy, setAgentBusy] = useState(false);
  const [agentActivity, setAgentActivity] = useState("L'agent est en train de traiter le document");
  const [activeDossierId, setActiveDossierId] = useState<string | null>(null);
  const [hitlBusy, setHitlBusy] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Clearance courante (ref pour rester à jour dans les callbacks SSE/RAG).
  const clearanceRef = useRef(clearance);
  clearanceRef.current = clearance;
  // 7.3-02 : dernière action utilisateur (texte ou fichier) pour retryLast().
  // Le File vit uniquement en mémoire (non persistable) — après un reload,
  // seul le retry de texte reste possible.
  const lastActionRef = useRef<{ kind: 'text'; text: string } | { kind: 'file'; file: File } | null>(null);
  // Miroir des messages pour les callbacks (resendMessage) sans dépendance d'état.
  const messagesRef = useRef<ChatMessage[]>([]);
  messagesRef.current = messages;
  // Dossiers dont la classification dépasse l'habilitation → contenu masqué.
  const restrictedRef = useRef<Set<string>>(new Set());

  // Désabonnements SSE actifs (nettoyés au démontage)
  const unsubsRef = useRef<Array<() => void>>([]);
  useEffect(
    () => () => {
      unsubsRef.current.forEach((fn) => fn());
      unsubsRef.current = [];
    },
    [],
  );

  // ── 7.3-05 / 7.3-06 : persistance locale du fil (ChatStore) ───────────────
  // Hydratation au montage (et à chaque changement de conversation),
  // sauvegarde debouncée (~300 ms) à chaque mutation.
  const hydratedRef = useRef(false);
  // Dossier actif en ref (pour flush() sans dépendance d'état).
  const dossierRef = useRef<string | null>(null);
  dossierRef.current = activeDossierId;

  /**
   * Rien de classifié restreint n'est écrit en clair : les messages rattachés
   * à un dossier dont la classification dépasse l'habilitation sont exclus.
   */
  const persist = useCallback(
    (msgs: ChatMessage[], dossierId: string | null): Promise<void> => {
      const persistable = msgs.filter((m) => {
        const dId = 'dossierId' in m ? m.dossierId : undefined;
        return !dId || !restrictedRef.current.has(dId);
      });
      if (typeof conversationId === 'string') {
        return chatStore.saveThread(conversationId, persistable, dossierId);
      }
      return chatStore.save(persistable, dossierId);
    },
    [conversationId],
  );

  useEffect(() => {
    if (conversationId === null) return; // liste de conversations pas encore prête
    hydratedRef.current = false;
    setHydrated(false);
    let cancelled = false;
    // Changement de fil : on coupe les flux SSE du fil précédent et on repart
    // d'un état neutre avant hydratation.
    unsubsRef.current.forEach((fn) => fn());
    unsubsRef.current = [];
    setMessages([]);
    setActiveDossierId(null);
    setAgentBusy(false);

    const loading =
      conversationId === undefined ? chatStore.load() : chatStore.loadThread(conversationId);
    loading
      .then((data) => {
        if (cancelled || !data) return;
        setMessages(data.messages);
        setActiveDossierId(data.activeDossierId);
      })
      .catch(() => {
        /* stockage indisponible → fil vierge */
      })
      .finally(() => {
        if (!cancelled) {
          hydratedRef.current = true;
          setHydrated(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    const timer = setTimeout(() => {
      void persist(messages, activeDossierId);
    }, 300);
    return () => clearTimeout(timer);
  }, [messages, activeDossierId, persist]);

  // ── Mutations de messages ─────────────────────────────────────────────────

  const push = useCallback((msg: ChatMessage) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  // Injection du message de bienvenue local — mode cowork uniquement (pas de dossier actif)
  useEffect(() => {
    if (hydrated && messages.length === 0 && config?.welcome_message && !activeDossierId) {
      push({
        id: 'welcome',
        author: 'agent',
        at: Date.now(),
        kind: 'text',
        text: config.welcome_message,
        tick: 'read',
      });
    }
  }, [hydrated, messages.length, config?.welcome_message, activeDossierId, push]);

  const patchDoc = useCallback((id: string, patch: Partial<DocumentMessage>) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === id && m.kind === 'document' ? { ...m, ...patch } : m)),
    );
  }, []);

  // ── Abonnement SSE pour un dossier ────────────────────────────────────────

  const subscribeStream = useCallback(
    (dossierId: string, docMsgId: string) => {
      setAgentBusy(true);
      setAgentActivity('Traitement du document en cours');

      const onEvent = (evt: ChatEvent) => {
        switch (evt.type) {
          case 'ingest':
          case 'ocr':
            patchDoc(docMsgId, { tick: 'delivered' });
            setAgentActivity(evt.type === 'ocr' ? 'OCR en cours' : 'Ingestion du document');
            break;
          case 'classify': {
            const c = extractClassification(evt.payload);
            if (c) patchDoc(docMsgId, { sensitivity: c });
            // Filtrage par habilitation : si la classification dépasse la
            // clearance de l'utilisateur, le dossier est marqué restreint.
            const level = parseClearance(c);
            if (level && !clearanceAllows(clearanceRef.current, level)) {
              restrictedRef.current.add(dossierId);
            }
            setAgentActivity('Classification du document');
            break;
          }
          case 'hitl': {
            setAgentBusy(false);
            const hitl: HitlMessage = {
              id: newId(),
              author: 'agent',
              at: Date.now(),
              kind: 'hitl',
              dossierId,
              text:
                str(evt.payload.message) ??
                'Le document contient des clauses inhabituelles. Veuillez vérifier et valider pour poursuivre.',
            };
            push(hitl);
            break;
          }
          case 'done': {
            patchDoc(docMsgId, { tick: 'read' });
            setAgentBusy(false);
            const isRestricted = restrictedRef.current.has(dossierId);
            const card: ResultCardMessage = isRestricted
              ? {
                  id: newId(),
                  author: 'agent',
                  at: Date.now(),
                  kind: 'result',
                  dossierId,
                  summary: '',
                  metadata: [],
                  restricted: true,
                  classification: extractClassification(evt.payload) ?? undefined,
                }
              : {
                  id: newId(),
                  author: 'agent',
                  at: Date.now(),
                  kind: 'result',
                  dossierId,
                  summary: extractSummary(evt.payload),
                  metadata: extractMetadata(evt.payload),
                };
            push(card);
            break;
          }
          case 'error': {
            setAgentBusy(false);
            const err: TextMessage = {
              id: newId(),
              author: 'agent',
              at: Date.now(),
              kind: 'text',
              tone: 'info',
              text:
                str(evt.payload.message) ??
                "Une erreur est survenue pendant le traitement du document.",
            };
            push(err);
            break;
          }
        }
      };

      const unsub = sdk.chat.subscribe(dossierId, onEvent, () => {
        if (demoMode) {
          // Démo : le SDK rejoue un scénario fictif — on garde le typing actif.
          setAgentActivity('Reconnexion — mode démonstration');
          return;
        }
        // Prod : pas de données fabriquées → on signale un état réel.
        setAgentBusy(false);
        push({
          id: newId(),
          author: 'agent',
          at: Date.now(),
          kind: 'text',
          tone: 'info',
          text: "Le flux temps réel est momentanément indisponible. Réessayez dans un instant.",
        });
      });
      unsubsRef.current.push(unsub);
    },
    [sdk, patchDoc, push, demoMode],
  );

  // ── Upload d'un fichier ───────────────────────────────────────────────────

  const attachFile = useCallback(
    (file: File) => {
      lastActionRef.current = { kind: 'file', file };
      const docId = newId();
      const docMsg: DocumentMessage = {
        id: docId,
        author: 'user',
        at: Date.now(),
        kind: 'document',
        fileName: file.name,
        sizeLabel: humanFileSize(file.size),
        progress: 5,
      };
      push(docMsg);

      // Progression optimiste (fetch n'expose pas la progression d'upload)
      let pct = 5;
      const ticker = setInterval(() => {
        pct = Math.min(90, pct + 15);
        patchDoc(docId, { progress: pct });
      }, 150);

      sdk.sessions
        .upload(file)
        .then(({ data }) => {
          clearInterval(ticker);
          patchDoc(docId, { progress: 100, tick: 'sent', dossierId: data.dossier_id });
          setActiveDossierId(data.dossier_id);
          subscribeStream(data.dossier_id, docId);
        })
        .catch((err: unknown) => {
          clearInterval(ticker);
          patchDoc(docId, { progress: 100 });
          const security = securityBlockText(err);
          push({
            id: newId(),
            author: 'agent',
            at: Date.now(),
            kind: 'text',
            tone: security ? 'security' : 'info',
            text: security ?? `Échec de l'envoi du document : ${(err as Error).message}`,
          });
        });
    },
    [sdk, push, patchDoc, subscribeStream],
  );

  // ── Upload conversationnel (CHAT-03) ─────────────────────────────────────

  const uploadAttachment = useCallback(
    async (file: File): Promise<AttachmentUploadResult> => {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);
      const resp = await fetch(
        `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1/chat/attachment`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData },
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({})) as { detail?: string };
        throw new Error(err.detail ?? `Erreur upload (${resp.status})`);
      }
      return resp.json() as Promise<AttachmentUploadResult>;
    },
    [getToken],
  );

  // ── Envoi d'un message texte (question RAG sur le dossier courant) ─────────

  const sendText = useCallback(
    (text: string, attachmentId?: string | null) => {
      lastActionRef.current = { kind: 'text', text };
      push({ id: newId(), author: 'user', at: Date.now(), kind: 'text', text, tick: 'sent' });

      // ── Mode cowork : aucun dossier actif → ConversationEngine
      if (!activeDossierId) {
        const agentMsgId = newId();
        push({ id: agentMsgId, author: 'agent', at: Date.now(), kind: 'text', text: '', tick: 'sending' });
        setAgentBusy(true);
        setAgentActivity('Réflexion…');

        (async () => {
          let fullText = '';
          try {
            const token = getToken();                     // depuis useAuth
            const sessionId = conversationId ?? 'default';
            const gen = conversationStream(
              {
                message: text,
                session_id: sessionId,
                dossier_id: null,
                // CHAT-03 : injecte l'id de la pièce jointe si présent
                ...(attachmentId ? { attachment_id: attachmentId } : {}),
              } as Parameters<typeof conversationStream>[0],
              token,
              import.meta.env.VITE_API_BASE_URL ?? '',
            );

            for await (const event of gen) {
              if (event.type === 'token') {
                // Premier token reçu → effacer l'activité (bulle de statut disparaît)
                if (fullText === '') setAgentActivity('');
                fullText += event.content;
                setMessages(prev =>
                  prev.map(m => m.id === agentMsgId
                    ? { ...m, text: fullText }
                    : m
                  )
                );
              } else if (event.type === 'status') {
                // CHAT-06b — mise à jour de la narration d'avancement agent
                setAgentActivity(event.text);
                // Stocker les _debug events dans la bulle agent (admin uniquement)
                if (event._debug != null) {
                  setMessages(prev =>
                    prev.map(m => m.id === agentMsgId && m.kind === 'text'
                      ? {
                          ...m,
                          statusDebugLog: [
                            ...((m as TextMessage).statusDebugLog ?? []),
                            { step: event.step as StatusDebugEntry['step'], debug: event._debug as StatusDebugEntry['debug'] },
                          ],
                        }
                      : m
                    )
                  );
                }
              } else if (event.type === 'tool') {
                // CHAT-06b — outil appelé par l'agent : affiche dans la narration
                setAgentActivity(`Outil : ${event.tool_name}…`);
                if (event.arguments != null) {
                  // Stocke dans statusDebugLog pour le panneau admin
                  setMessages(prev =>
                    prev.map(m => m.id === agentMsgId && m.kind === 'text'
                      ? {
                          ...m,
                          statusDebugLog: [
                            ...((m as TextMessage).statusDebugLog ?? []),
                            { step: 'tool' as StatusDebugEntry['step'], debug: { tool: event.tool_name, ...event.arguments as Record<string, string> } },
                          ],
                        }
                      : m
                    )
                  );
                }
              } else if (event.type === 'usage') {
                // CHAT-07 — attacher les métriques FinOps à la bulle agent
                setMessages(prev =>
                  prev.map(m => m.id === agentMsgId && m.kind === 'text'
                    ? {
                        ...m,
                        usage: {
                          model_tier: event.model_tier,
                          tokens_used: event.tokens_used,
                          cost_eur: event.cost_eur,
                          zero_token: event.zero_token,
                          model_id: event.model_id,
                        },
                      }
                    : m
                  )
                );
              } else if (event.type === 'attachment') {
                // CHAT-03 — gain de nettoyage de la pièce jointe : message info discret
                const infoText =
                  `📎 ${event.filename} · −${event.gain_pct}% (${event.level})` +
                  ` · ~${event.tok_after.toLocaleString('fr-FR')} tok estimés`;
                const infoMsg: TextMessage = {
                  id: `att-${Date.now()}`,
                  kind: 'text',
                  author: 'agent',
                  tone: 'info',
                  text: infoText,
                  at: Date.now(),
                };
                // Insérer juste avant la bulle agent en cours (dernière position)
                setMessages(prev => {
                  const last = prev[prev.length - 1];
                  return last ? [...prev.slice(0, -1), infoMsg, last] : [...prev, infoMsg];
                });
              } else if (event.type === 'done') {
                fullText = event.text || fullText;
                setMessages(prev =>
                  prev.map(m => m.id === agentMsgId
                    ? { ...m, text: fullText, tick: 'read' }
                    : m
                  )
                );
              } else if (event.type === 'command') {
                setMessages(prev =>
                  prev.map(m => m.id === agentMsgId
                    ? { ...m, text: event.result, tone: 'info', tick: 'read' }
                    : m
                  )
                );
              } else if (event.type === 'error') {
                setMessages(prev =>
                  prev.map(m => m.id === agentMsgId
                    ? { ...m, text: `Erreur : ${event.detail}`, tone: 'info', tick: 'read' }
                    : m
                  )
                );
              }
            }
          } catch (err) {
            const security = securityBlockText(err);
            setMessages(prev =>
              prev.map(m => m.id === agentMsgId
                ? { ...m, text: security ?? `Erreur : ${(err as Error).message}`, tone: security ? 'security' : 'info', tick: 'read' }
                : m
              )
            );
          } finally {
            setAgentBusy(false);
          }
        })();
        return;
      }

      // ── Mode RAG : dossier actif → comportement existant (inchangé)
      if (restrictedRef.current.has(activeDossierId)) {
        push({
          id: newId(), author: 'agent', at: Date.now(), kind: 'text', tone: 'info',
          text: "Accès restreint : la classification de ce document dépasse votre habilitation.",
        });
        return;
      }

      setAgentBusy(true);
      setAgentActivity('Recherche dans le document');

      sdk.rag
        .query({ dossier_id: activeDossierId, question: text, top_k: 5 })
        .then(({ data }: AssistiaSDKResponse<RagQueryResponse>) => {
          setAgentBusy(false);
          const top = data.results[0];
          const score = top ? ` (Score : ${Math.round(top.score * 100)}%)` : '';
          const answer =
            data.context_text?.trim() ||
            str(top?.metadata.text) ||
            "Je n'ai pas trouvé d'information correspondante dans ce document.";
          push({ id: newId(), author: 'agent', at: Date.now(), kind: 'text', text: `${answer}${score}` });
        })
        .catch((err: unknown) => {
          setAgentBusy(false);
          const security = securityBlockText(err);
          push({
            id: newId(), author: 'agent', at: Date.now(), kind: 'text',
            tone: security ? 'security' : 'info',
            text: security ?? `La requête a échoué : ${(err as Error).message}`,
          });
        });
    },
    [sdk, activeDossierId, conversationId, getToken, push],
  );

  // ── Décision HITL ─────────────────────────────────────────────────────────

  const decideHitl = useCallback(
    (dossierId: string, approved: boolean) => {
      setHitlBusy(true);
      sdk.suspensions
        .validate(dossierId, approved)
        .then(() => {
          const at = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
          setMessages((prev) =>
            prev.map((m) =>
              m.kind === 'hitl' && m.dossierId === dossierId && !m.resolution
                ? { ...m, resolution: { decision: approved ? 'approved' : 'rejected', at } }
                : m,
            ),
          );
          push({
            id: newId(),
            author: 'agent',
            at: Date.now(),
            kind: 'text',
            tone: 'info',
            text: approved
              ? 'Validation enregistrée. Je reprends le traitement du document.'
              : 'Document rejeté. Le traitement est interrompu.',
          });
          // Le flux SSE d'origine reste ouvert : le backend reprend et émettra
          // l'événement 'done'. Pas de ré-abonnement (éviterait des doublons).
        })
        .catch((err: unknown) => {
          push({
            id: newId(),
            author: 'agent',
            at: Date.now(),
            kind: 'text',
            tone: 'info',
            text: `La validation a échoué : ${(err as Error).message}`,
          });
        })
        .finally(() => setHitlBusy(false));
    },
    [sdk, push],
  );

  // ── 7.3-02 : réessayer ────────────────────────────────────────────────────

  /** Relance la dernière requête utilisateur (texte RAG ou upload). */
  const retryLast = useCallback(() => {
    const last = lastActionRef.current;
    if (!last) return;
    if (last.kind === 'text') {
      sendText(last.text);
    } else {
      attachFile(last.file);
    }
  }, [sendText, attachFile]);

  /** Ré-émet le texte d'un message utilisateur existant (identifié par id). */
  const resendMessage = useCallback(
    (id: string) => {
      const msg = messagesRef.current.find((m) => m.id === id);
      if (msg && msg.kind === 'text' && msg.author === 'user') {
        sendText(msg.text);
      }
    },
    [sendText],
  );

  // ── 7.3-06 : sauvegarde immédiate (avant bascule de conversation) ─────────

  const flush = useCallback(async () => {
    if (!hydratedRef.current) return;
    await persist(messagesRef.current, dossierRef.current);
  }, [persist]);

  return {
    messages,
    agentBusy,
    agentActivity,
    activeDossierId,
    hitlBusy,
    sendText,
    attachFile,
    uploadAttachment,
    decideHitl,
    retryLast,
    resendMessage,
    flush,
  };
}
